import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getStoreSettings } from '../../api/waiting.api';
import { issueMucketSsoToken } from '../../api/integration.api';
import styles from './Sidebar.module.css';

interface NavItem {
  to: string;
  label: string;
  /** takeoutEnabled === false 일 때 비활성화 */
  requiresTakeout?: boolean;
  /** OWNER 만 접근 가능 (옵션 A 권한 모델) */
  ownerOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: '대기관리' },
  { to: '/report', label: '통계', ownerOnly: true },
  { to: '/credits', label: '크레딧 충전', ownerOnly: true },
  { to: '/credits/history', label: '사용 내역' },
  { to: '/settings/store', label: '매장 설정', ownerOnly: true },
  { to: '/settings/menu', label: '메뉴 설정', requiresTakeout: true },
  { to: '/settings/account', label: '계정 설정' },
  { to: '/inquiry', label: '문의/건의' },
];

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ isMobileOpen = false, onMobileClose }: SidebarProps) {
  const { logout, storeId, isDemo, licenseKey, isOwner, role, memberName } = useAuth();
  const [takeoutEnabled, setTakeoutEnabled] = useState<boolean | null>(isDemo ? true : null);
  const [isMucketLinking, setIsMucketLinking] = useState(false);

  const handleMucketLink = async () => {
    if (isMucketLinking) return;
    if (isDemo) {
      window.alert('데모 모드에서는 먹킷 연동을 이용할 수 없습니다.');
      return;
    }
    if (!licenseKey) {
      window.alert('로그인 정보를 확인할 수 없습니다.');
      return;
    }
    setIsMucketLinking(true);
    try {
      const result = await issueMucketSsoToken(licenseKey);
      window.open(result.redirectUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      window.alert(e?.message ?? '먹킷 대시보드 연결에 실패했습니다.');
    } finally {
      setIsMucketLinking(false);
    }
  };

  useEffect(() => {
    if (!storeId) return;
    if (isDemo) {
      // 데모 모드는 포장 ON 가정 → 메뉴 설정 활성화
      setTakeoutEnabled(true);
      return;
    }
    let cancelled = false;
    getStoreSettings(storeId)
      .then((s) => {
        if (!cancelled) setTakeoutEnabled(Boolean(s.takeoutEnabled));
      })
      .catch(() => {
        if (!cancelled) setTakeoutEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storeId, isDemo]);

  return (
    <aside className={`${styles.sidebar} ${isMobileOpen ? styles.sidebarMobileOpen : ''}`}>
      <div className={styles.logo}>
        <div className={styles.logoTitle}>만석</div>
        <div className={styles.logoSubtitle}>매장 관리</div>
        {/* 모바일 닫기 버튼 */}
        <button
          type="button"
          className={styles.mobileClose}
          onClick={onMobileClose}
          aria-label="메뉴 닫기"
        >
          ×
        </button>
      </div>
      <nav className={styles.nav}>
        {role === 'MEMBER' && (
          <div className={styles.roleBadge}>
            <span>추가 관리자</span>
            {memberName && <small>{memberName}</small>}
          </div>
        )}
        {NAV_ITEMS.map((item) => {
          // OWNER 전용 항목은 MEMBER 에게 보여주되 비활성화 (혼란 방지)
          const ownerOnlyDisabled = item.ownerOnly && !isOwner && role !== null;
          const takeoutDisabled = item.requiresTakeout && takeoutEnabled === false;
          if (takeoutDisabled) {
            return (
              <span
                key={item.to}
                className={`${styles.navLink} ${styles.navLinkDisabled}`}
                title="포장 기능이 꺼져 있습니다. 매장 설정에서 활성화해주세요."
              >
                {item.label}
              </span>
            );
          }
          if (ownerOnlyDisabled) {
            return (
              <span
                key={item.to}
                className={`${styles.navLink} ${styles.navLinkDisabled}`}
                title="사장님(OWNER) 계정만 접근 가능한 메뉴입니다."
              >
                {item.label}
                <span className={styles.lockIcon} aria-hidden="true">🔒</span>
              </span>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
              }
            >
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className={styles.integrationGroup}>
        <div className={styles.integrationLabel}>외부 연동</div>
        <button
          type="button"
          className={styles.mucketLink}
          onClick={handleMucketLink}
          disabled={isMucketLinking}
          title="사업자번호 기반 자동 로그인"
        >
          {isMucketLinking ? '연결 중...' : '먹킷 사업주 대시보드 →'}
        </button>
      </div>
      <div className={styles.footer}>
        <button className={styles.logoutBtn} onClick={logout}>
          로그아웃
        </button>
      </div>
    </aside>
  );
}
